// DRAFT — REVIEW ARTIFACT — NOT DEPLOYED
//
// THE REVIEW-BEFORE-SEND SURFACE.
//
// The retail newsletter is the only auto-sending output in the estate and it is a retail
// financial promotion. Until this tab existed, the ONLY human act in its path was
// approve_reporting_gate — and that act approves NUMBERS AND HASHES: GateTab shows
// reconciliation, blocking flags and a presence hint for frozen artifacts, and ArtifactsTab
// explicitly never fetches object bytes. The gate signature was therefore cryptographically
// bound to bytes no human had ever seen.
//
// This tab is the second, DISTINCT act: a named human reads the exact rendered copy AND the
// send envelope, ticks the server-owned checklist, and records `review_content` on the
// append-only log bound to (artifact_sha256, ir_hash, envelope_sha256).
//
// WHAT THIS TAB IS NOT: it is not the wall. The wall is step 4 of reporting_deliveries_guard()
// (0003i §8) — it fires for every role including service_role and owner, below the worker, so no
// code path can reserve a send for unreviewed copy. Everything here is the surface that makes the
// act possible and honest. A disabled button is UX; the RPC and the trigger re-derive everything.
//
// HONEST STATUS, RE-MEASURED 2026-08-20. This control has still never fired, but almost
// every reason previously given here was wrong by 2026-08-14 and this block asserted them
// unchanged until 2026-08-20. Corrected, with what was actually measured:
//
//   * The renderer EXISTS and is DEPLOYED. supabase/functions/reporting-render-html has been
//     ACTIVE since 2026-08-14; its deployed bytes are sha256-identical to the repo file.
//   * RENDER_HTML_URL IS SET and resolves to it. Cycle ec8e622a's last_error is a
//     `dispatch_422` carrying a structured render-engine body, and the worker's unset-guard
//     throws a distinct StageFailure BEFORE any dispatch — so a 422 is only reachable with
//     the endpoint configured and answering. Edge logs show the POST directly.
//   * 0003i IS APPLIED. All four review RPCs exist and resolve. The old note claiming state
//     (a) was "the state production is actually in, VERIFIED LIVE 2026-08-11" is stale.
//   * There is no template object to upload. The deployed engine builds from an in-code
//     TEMPLATE_SPEC and performs no storage download. That requirement belonged to
//     services/render-html, a superseded parallel implementation the worker does not call.
//
// WHAT IS ACTUALLY TRUE: no newsletter has ever been frozen, because the render engine has
// so far REFUSED every document it was asked to build — on content, not on infrastructure.
// That is the fail-closed path working. The refusal reasons are per-cycle and change with
// each tick, which is exactly why this screen must read them from the cycle row instead of
// restating a snapshot. See RenderStatusCard.
//
// THE THREE REFUSAL STATES, none of them a blank screen and none of them a fake preview:
//   (a) review RPCs absent -> 0003i has not run in THIS database. Not production's state
//                             today, but a real state for any database where it has not run.
//   (b) no frozen artifact -> nothing has been frozen for this cycle. The card names the
//                             engine's own blocking reasons, read from last_error.
//   (c) bytes unreachable  -> a frozen row exists but no signer can hand us its bytes. Show
//                             the metadata, show the envelope, show the log — and BLOCK the
//                             review. reporting-artifact-fetch was deployed 2026-08-20 to
//                             close the ordinary cause of this; it now means a real fault.
//
// The tab does NOT gate the Gate. Approving the numbers is not approving the copy and neither
// implies the other; fusing them would undo the separation this control exists to create.

import { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, FileWarning, ShieldAlert, ShieldCheck } from "lucide-react";
import { LoadingState, ErrorState, EmptyState } from "@/components/research-hub";
import {
  ContentReviewReceipt,
  NewsletterPreviewFrame,
  ReportingErrorBoundary,
  SendEnvelopeCard,
  SensitivityTierBadge,
  newsletterCheckLabel,
  shortHash,
} from "@/components/investment-reporting";
import {
  isMissingReviewCapability,
  useNewsletterPreview,
  useNewsletterReviewState,
  useRecordNewsletterContentReview,
  useRevokeNewsletterContentReview,
} from "@/hooks/investment-reporting/useNewsletterReview";
import type { CycleContext } from "./ReportingCycleLayout";

/**
 * WHY THE RENDER HAS NOT PRODUCED BYTES — read from the cycle, not from a constant.
 *
 * WHAT THIS REPLACED, AND WHY IT HAD TO GO. This card used to print a fixed list of three
 * "missing pieces": that the newsletter render service did not exist, that RENDER_HTML_URL
 * was "unset and undeployed", and that a template object had to be uploaded to
 * reporting-artifacts/templates/. All three were true when they were written on 2026-08-11.
 * All three were false by 2026-08-14, and the card went on asserting them for six days,
 * unconditionally, on every artifact-less cycle:
 *
 *   * reporting-render-html was deployed 2026-08-14 and is ACTIVE; its deployed bytes are
 *     sha256-identical to the file in this repo.
 *   * RENDER_HTML_URL is SET. The proof is in the cycle row itself: last_error carries a
 *     `dispatch_422` whose body is a structured render-engine response, and the worker's
 *     unset-guard throws a different, distinct StageFailure BEFORE any dispatch — so a 422
 *     is only reachable with the endpoint configured and answering.
 *   * the deployed engine reads no template object at all; it builds from an in-code
 *     TEMPLATE_SPEC. That requirement belonged to services/render-html, a superseded
 *     parallel implementation that is not what the worker talks to.
 *
 * A hardcoded diagnosis is a diagnosis that cannot be wrong at the time of writing and
 * cannot be right for long. This card now reads the cycle's OWN last_error and shows the
 * blocking map the render engine actually returned, so the screen can only ever be as stale
 * as the last tick. When it cannot parse one it says so plainly rather than inventing a
 * reason — the failure mode is silence, never a confident falsehood.
 */
function parseDispatchBlocking(lastError: string | null | undefined): {
  status: string | null;
  blocking: Record<string, string[]> | null;
  detail: string | null;
  truncated: boolean;
  raw: string | null;
} {
  const raw = typeof lastError === "string" && lastError.trim() ? lastError : null;
  if (!raw) return { status: null, blocking: null, detail: null, truncated: false, raw: null };

  const m = /^dispatch_(\d{3}):\s*(.*)$/s.exec(raw);
  if (!m) return { status: null, blocking: null, detail: null, truncated: false, raw };

  const status = m[1];
  const body = m[2];
  // The worker stores only the first 400 characters of the response body
  // (_shared/reporting-cycle-worker.ts), so the JSON is very often cut mid-token. Parse what
  // we can and SAY when it was clipped, rather than showing half a sentence as if complete.
  try {
    const parsed = JSON.parse(body);
    const blocking =
      parsed && typeof parsed.blocking === "object" && parsed.blocking !== null
        ? (Object.fromEntries(
            Object.entries(parsed.blocking as Record<string, unknown>).map(([k, v]) => [
              k,
              Array.isArray(v) ? v.map(String) : [String(v)],
            ]),
          ) as Record<string, string[]>)
        : null;
    return {
      status,
      blocking,
      detail: typeof parsed?.detail === "string" ? parsed.detail : null,
      truncated: false,
      raw,
    };
  } catch {
    // Truncated JSON. Recover the keys and whatever text follows them — that is still the
    // engine's own words, which beats ours.
    const keys = [...body.matchAll(/"([a-z_]+)":\s*\[\s*"([^"]*)/g)].map((mm) => [
      mm[1],
      mm[2],
    ]);
    const blocking = keys.length
      ? (keys.reduce<Record<string, string[]>>((acc, [k, v]) => {
          (acc[k] ??= []).push(v);
          return acc;
        }, {}))
      : null;
    return { status, blocking, detail: null, truncated: true, raw };
  }
}

const BLOCKING_LABELS: Record<string, string> = {
  missing_required_prose: "Required prose slots are missing",
  unresolved_cell_ratio: "Too many figures did not resolve",
  send_envelope_incomplete: "The send envelope is incomplete",
  foreign_frozen_version: "A foreign frozen version exists for this cycle",
  prose_bare_numbers: "Prose carries an ungrounded number",
};

function RenderStatusCard({ cycle }: { cycle: CycleContext["cycle"] }) {
  const parsed = parseDispatchBlocking(cycle.last_error);

  return (
    <Card className="p-4 space-y-3">
      <div className="font-heading text-sm font-medium text-foreground">
        Why there is nothing to review
      </div>

      {parsed.blocking ? (
        <>
          <p className="font-body text-xs text-muted-foreground">
            The render engine was reached and it <span className="font-medium text-foreground">refused</span>{" "}
            this document (HTTP {parsed.status}). Nothing was uploaded and nothing was frozen —
            that is the fail-closed path working, not an outage. It reported:
          </p>
          <ul className="font-body text-xs text-muted-foreground space-y-2 list-disc pl-4">
            {Object.entries(parsed.blocking).map(([key, items]) => (
              <li key={key}>
                <span className="font-medium text-foreground">
                  {BLOCKING_LABELS[key] ?? key}
                </span>
                <ul className="list-none pl-0 pt-1 space-y-1">
                  {items.map((item, i) => (
                    <li key={i} className="font-mono text-[11px] break-words">
                      {item}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
          {parsed.truncated && (
            <p className="font-body text-[11px] text-muted-foreground">
              The stored error is capped at 400 characters by the worker, so the last entry
              above may be cut off. The full response was not retained.
            </p>
          )}
          {parsed.detail && (
            <p className="font-body text-[11px] text-muted-foreground">{parsed.detail}</p>
          )}
        </>
      ) : parsed.raw ? (
        <>
          <p className="font-body text-xs text-muted-foreground">
            The cycle recorded this error on its last run. It is not in the render engine's
            structured form, so it is shown verbatim rather than interpreted:
          </p>
          <pre className="font-mono text-[11px] text-muted-foreground whitespace-pre-wrap break-words bg-muted/40 rounded p-2">
            {parsed.raw}
          </pre>
        </>
      ) : (
        <p className="font-body text-xs text-muted-foreground">
          This cycle has recorded no render error. Either the render stage has not run yet, or
          it ran and produced nothing without saying why — check the cycle's stage cursor
          (currently <code className="font-mono">{cycle.current_stage ?? "unknown"}</code>,
          status <code className="font-mono">{cycle.status ?? "unknown"}</code>) before
          assuming a defect on this screen.
        </p>
      )}

      <p className="font-body text-[11px] text-muted-foreground pt-1">
        A newsletter becomes reviewable when the render stage completes and freezes an
        artifact. Until it does, no review can be recorded and every send refuses — the
        enforcement is in the database (reporting_deliveries_guard step 4), not on this
        screen.
      </p>
    </Card>
  );
}

function NewsletterTabInner() {
  const { cycle, canSign } = useOutletContext<CycleContext>();
  const { data, isLoading, error, refetch } = useNewsletterReviewState(cycle.id);
  const state = data?.state ?? null;
  const preview = useNewsletterPreview(cycle.id, state);
  const record = useRecordNewsletterContentReview();
  const revoke = useRevokeNewsletterContentReview();

  const [note, setNote] = useState("");
  const [checked, setChecked] = useState<Record<string, boolean>>({});

  // The checklist is an attestation about SPECIFIC bytes. If the artifact changes underneath the
  // reviewer (a re-render), every tick is void — reset rather than carry ticks across content.
  const artifactSha = state?.artifact_sha256 ?? null;
  useEffect(() => {
    setChecked({});
    setNote("");
  }, [artifactSha]);

  // The checklist keys are SERVER-owned (reporting_newsletter_review_checklist). A client that
  // shipped a shorter list simply could not produce an accepted review — so render whatever the
  // server sent, including keys this build has never heard of.
  // useNewsletterReview normalises this to a string[]; the Array.isArray guard is belt-and-braces
  // because .every()/.map() on a non-array here would throw during render.
  const checklistKeys = useMemo(
    () => (Array.isArray(state?.checklist) ? state.checklist : []),
    [state?.checklist],
  );
  const allTicked = checklistKeys.length > 0 && checklistKeys.every((k) => checked[k] === true);

  if (isLoading) return <LoadingState rows={3} />;
  if (error) return <ErrorState onRetry={() => refetch()} />;

  // --- (a) the control itself is not installed --------------------------------
  //
  // 0003i IS APPLIED in this project — all four RPCs exist and resolve, verified live
  // 2026-08-20. This branch is therefore not the state production is in, and the header
  // comment's "VERIFIED LIVE 2026-08-11" note is stale. It is kept because the predicate is
  // narrow and honest (it fires only on PGRST202 / 404 for these specific names), so a
  // database where the migration has NOT run still gets a true answer instead of a crash.
  if (data?.rpcMissing) {
    return (
      <div className="space-y-4">
        <EmptyState
          icon={<FileWarning className="h-7 w-7" aria-hidden />}
          title="Review-before-send is not available in this database."
          body="The 0003i review RPCs do not exist here, so no content review can be recorded or withdrawn and the enforcement in reporting_deliveries_guard step 4 is absent. Nothing on this screen is a preview, a placeholder, or a partially-loaded state."
        />
        <Card className="p-4 space-y-2">
          <div className="font-heading text-sm font-medium text-foreground">What is missing</div>
          <p className="font-body text-xs text-muted-foreground">
            <span className="font-medium text-foreground">
              The content-review migration is not applied.
            </span>{" "}
            <code className="font-mono">reporting_newsletter_review_state</code>,{" "}
            <code className="font-mono">record_newsletter_content_review</code> and{" "}
            <code className="font-mono">revoke_newsletter_content_review</code> do not exist in
            this database. Applying{" "}
            <code className="font-mono">
              docs/investment-reporting/0003i_newsletter_content_review.sql
            </code>{" "}
            creates them along with the{" "}
            <code className="font-mono">reporting_deliveries_guard</code> step 4 that actually
            enforces the review. Until that step exists,{" "}
            <span className="font-medium text-foreground">
              nothing on this screen is protecting the send path
            </span>{" "}
            — the enforcement lives in the database, not here.
          </p>
        </Card>
      </div>
    );
  }

  if (!state) return <ErrorState onRetry={() => refetch()} />;

  // --- (b) nothing has ever been rendered -------------------------------------
  if (!state.has_artifact) {
    return (
      <div className="space-y-4">
        <EmptyState
          icon={<FileWarning className="h-7 w-7" aria-hidden />}
          title="No newsletter has been frozen for this cycle — there is nothing to review."
          body="A content review binds to exact bytes. With no frozen artifact there is nothing to bind to, no review can be recorded, and the send refuses. That refusal is correct and safe. The reason the render produced nothing is below, read from this cycle's own last recorded error rather than from a fixed list."
        />
        <RenderStatusCard cycle={cycle} />
        {(state.reasons ?? []).length > 0 && (
          <Card className="p-4 space-y-2">
            <div className="font-body text-xs font-medium text-foreground">
              The server also reports
            </div>
            <ul className="font-body text-xs text-muted-foreground space-y-1">
              {(state.reasons ?? []).map((r, i) => (
                <li key={i} className="flex items-start gap-1.5">
                  <ShieldAlert className="h-3 w-3 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>
    );
  }

  // --- (c) full surface --------------------------------------------------------
  const delivered = !!state.delivered_at;
  const envelopePinned = !!state.envelope_sha256 && !!state.send_envelope;
  const irMatches =
    !!state.ir_sha256 && !!state.current_ir_sha256 && state.ir_sha256 === state.current_ir_sha256;
  const previewOk = preview.data?.integrityOk === true;
  const newestIsReview = state.review?.action === "review_content";

  // Every condition below is ALSO re-derived by record_newsletter_content_review; this is the
  // readable half. `previewOk` is the one condition the server cannot check for us — it is the
  // assertion that the human was actually shown these bytes.
  const blockers: string[] = [];
  if (delivered) blockers.push("this newsletter has already been delivered — a delivered promotion cannot be retrospectively reviewed");
  if (!irMatches) blockers.push("the frozen bytes were built from a different IR than the cycle currently holds — re-render first");
  if (!envelopePinned) blockers.push("the frozen artifact pins no send envelope — re-render with the envelope-recording render stage");
  if (preview.isLoading) blockers.push("still fetching the rendered copy");
  else if (!previewOk) blockers.push("the rendered copy is not displayed and verified on this screen");
  if (!allTicked) blockers.push("every item on the server checklist must be confirmed");

  // record_newsletter_content_review is Investment, Operations OR an admin since Sam's first
  // ruling of 2026-08-17 (migration 0003s). Measured live, it opens with
  //   IF NOT (is_investment_team() OR is_operations_team() OR is_admin_or_superadmin(_uid))
  // which is exactly can_review_reporting(), so this follows canSign, the server's own answer.
  const canRecord = canSign && blockers.length === 0 && !record.isPending;

  const onRecord = () => {
    if (!cycle.id || !state.artifact_sha256) return;
    const payload: Record<string, boolean> = {};
    for (const k of checklistKeys) payload[k] = checked[k] === true;
    record.mutate({
      cycleId: cycle.id,
      artifactSha256: state.artifact_sha256,
      checklist: payload,
      note: note || null,
    });
  };

  const onRevoke = () => {
    if (!cycle.id || !state.artifact_sha256) return;
    revoke.mutate({
      cycleId: cycle.id,
      artifactSha256: state.artifact_sha256,
      note: note || null,
    });
  };

  // Export-for-send (the manual-send path). Gated on the SERVER's `reviewed`, not on the local
  // checklist — and honestly labelled as friction rather than enforcement: see the note below.
  const onDownload = () => {
    const html = preview.data?.html;
    if (!html) return;
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `newsletter-${cycle.period_label ?? "cycle"}-${(state.artifact_sha256 ?? "").slice(0, 12)}.html`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header: what these bytes are */}
      <Card className="p-4 flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-heading text-sm font-medium text-foreground">Retail newsletter</span>
            {state.reviewed ? (
              <span className="inline-flex items-center gap-1 font-body text-[11px] text-primary">
                <ShieldCheck className="h-3 w-3" aria-hidden /> copy reviewed
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 font-body text-[11px] text-destructive">
                <ShieldAlert className="h-3 w-3" aria-hidden /> copy NOT reviewed — send refuses
              </span>
            )}
          </div>
          <div className="font-body text-[11px] text-muted-foreground break-all">
            sha256 {shortHash(state.artifact_sha256, 16)}
            {state.bytes != null ? ` · ${state.bytes} bytes` : ""}
            {state.content_type ? ` · ${state.content_type}` : ""}
          </div>
          <div className="font-body text-[11px] text-muted-foreground break-all">
            built from IR {shortHash(state.ir_sha256, 12)} · cycle currently holds{" "}
            {shortHash(state.current_ir_sha256, 12)}
            {irMatches ? " (match)" : " (MISMATCH — re-render)"}
          </div>
          {delivered && (
            <div className="font-body text-[11px] text-destructive">
              Already delivered at {state.delivered_at}
            </div>
          )}
        </div>
        <SensitivityTierBadge tier="retail_public" />
      </Card>

      {/* 1. The rendered email itself + the integrity strip */}
      <section className="space-y-2">
        <h3 className="font-heading text-base font-medium text-foreground">The copy that would be sent</h3>
        <NewsletterPreviewFrame preview={preview.data} isLoading={preview.isLoading} />
      </section>

      {/* 2. The send envelope — reviewed as part of the same act */}
      <section className="space-y-2">
        <h3 className="font-heading text-base font-medium text-foreground">Subject, sender and audience</h3>
        <SendEnvelopeCard envelope={state.send_envelope} envelopeSha256={state.envelope_sha256} />
      </section>

      {/* 3. Server-owned checklist + 4. note + 5. the two acts */}
      <section className="space-y-2">
        <h3 className="font-heading text-base font-medium text-foreground">Record the review</h3>

        {!canSign ? (
          <Card className="p-4">
            <p className="font-body text-sm text-muted-foreground">
              Reviewing retail copy is for the Investment team, the Operations team, or an admin.
              The RPC additionally refuses any actor without a human auth.uid(), so service_role
              cannot review copy either.
            </p>
          </Card>
        ) : (
          <Card className="p-4 space-y-4">
            <ul className="space-y-2">
              {checklistKeys.map((k) => (
                <li key={k}>
                  <label className="flex items-start gap-2 font-body text-sm text-foreground">
                    <input
                      type="checkbox"
                      checked={checked[k] === true}
                      onChange={(e) => setChecked((c) => ({ ...c, [k]: e.target.checked }))}
                      disabled={!previewOk || delivered}
                      className="mt-1"
                    />
                    <span>{newsletterCheckLabel(k)}</span>
                  </label>
                </li>
              ))}
              {checklistKeys.length === 0 && (
                <li className="font-body text-xs text-muted-foreground">
                  Not found — the server returned no checklist. A review cannot be recorded against
                  an empty definition of "complete".
                </li>
              )}
            </ul>

            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Reviewer note (recorded on the append-only approvals log)…"
              className="w-full min-h-[72px] rounded-md border border-input bg-background px-3 py-2 font-body text-sm text-foreground"
            />

            {blockers.length > 0 && (
              <ul className="font-body text-xs text-muted-foreground space-y-1">
                {blockers.map((b, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <ShieldAlert className="h-3 w-3 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}

            <div className="flex flex-wrap gap-2">
              <Button
                onClick={onRecord}
                disabled={!canRecord}
                title={
                  blockers.length > 0
                    ? `Blocked — ${blockers.join("; ")}`
                    : "Record that you have read this exact copy and envelope"
                }
              >
                {record.isPending
                  ? "Recording…"
                  : newestIsReview
                    ? "Re-record content review"
                    : "Record content review"}
              </Button>
              <Button
                variant="outline"
                onClick={onRevoke}
                disabled={!newestIsReview || revoke.isPending}
                title={
                  newestIsReview
                    ? "Withdraw the review — appends a withdrawal row; the send refuses again immediately"
                    : "There is no standing review to withdraw"
                }
              >
                {revoke.isPending ? "Withdrawing…" : "Withdraw review"}
              </Button>
            </div>

            {/* A PARTIAL 0003i application — the read RPC resolving but a write RPC missing —
                would otherwise surface as a raw PostgREST schema-cache string. Name the real
                cause instead, and never imply the act succeeded. */}
            {record.isError && (
              <p className="font-body text-xs text-destructive">
                {isMissingReviewCapability(record.error)
                  ? "No review was recorded: record_newsletter_content_review does not exist in this database. The content-review migration (0003i) is only partially applied — the read side resolves but the write side is missing."
                  : ((record.error as any)?.message ?? "The review RPC refused this act.")}
              </p>
            )}
            {revoke.isError && (
              <p className="font-body text-xs text-destructive">
                {isMissingReviewCapability(revoke.error)
                  ? "Nothing was withdrawn: revoke_newsletter_content_review does not exist in this database. The content-review migration (0003i) is only partially applied. Any standing review is still standing."
                  : ((revoke.error as any)?.message ?? "The withdrawal RPC refused this act.")}
              </p>
            )}

            <p className="font-body text-[11px] text-muted-foreground">
              The button state is UX only. The RPC re-derives everything — a frozen newsletter with
              these exact bytes, not already delivered, built from the cycle's current IR, a complete
              checklist, and a pinned envelope — and the delivery guard re-checks the whole thing
              again at reserve, for every role. The one check only this screen can make is that the
              bytes above are the bytes named: that is why a hash mismatch blocks with no override.
            </p>
          </Card>
        )}
      </section>

      {/* 6. The receipt + the server's own refusal reasons */}
      <section className="space-y-2">
        <h3 className="font-heading text-base font-medium text-foreground">Review log</h3>
        <ContentReviewReceipt review={state.review} />
        {(state.reasons ?? []).length > 0 && (
          <Card className="p-4">
            <div className="font-body text-xs font-medium text-foreground mb-1">
              Why the send is currently refused
            </div>
            <ul className="font-body text-xs text-muted-foreground space-y-1">
              {(state.reasons ?? []).map((r, i) => (
                <li key={i} className="flex items-start gap-1.5">
                  <ShieldAlert className="h-3 w-3 mt-0.5 shrink-0 text-destructive" aria-hidden />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </section>

      {/* 7. The manual-send path — stated as friction, never as enforcement */}
      <section className="space-y-2">
        <h3 className="font-heading text-base font-medium text-foreground">Export for manual send</h3>
        <Card className="p-4 space-y-3">
          <Button
            variant="outline"
            onClick={onDownload}
            disabled={!state.reviewed || !previewOk}
            title={
              state.reviewed && previewOk
                ? "Download the exact reviewed bytes"
                : "Blocked — the copy must be reviewed (and displayed here, verified) before it can be exported for sending"
            }
          >
            <Download className="h-3.5 w-3.5 mr-2" aria-hidden /> Download reviewed HTML
          </Button>
          <p className="font-body text-[11px] text-muted-foreground">
            Reading the copy is deliberately ungated — a reviewer must be able to read the bytes in
            order to review them. Only the export is gated. Be clear about what that is worth: the
            current live process is a human pasting HTML into Mailchimp by hand, and nothing in the
            gate, the delivery guard, or this tab constrains that path. No delivery row is written
            for a hand-send, so the write-once delivered stamp never applies. This button is
            enforcement-by-friction, not enforcement — a determined human can screenshot and retype.
            The only complete fix is to make the automated send the sole path.
          </p>
        </Card>
      </section>
    </div>
  );
}

/**
 * The routed component. The tab body is wrapped in a LOCAL error boundary.
 *
 * There is no error boundary anywhere else in src/ (verified 2026-08-11), and React 18 unmounts
 * the entire root tree on an uncaught render error — so without this, one unexpected shape from
 * one RPC on this tab white-screens the whole SPA for that user, not just the newsletter tab.
 * The boundary is scoped here rather than added globally so that real bugs elsewhere still fail
 * loudly instead of being silently swallowed app-wide.
 *
 * The tab is deliberately NOT hidden while the control is unbuilt. A vanished tab reads as
 * "nothing to do here", when the truth is the opposite: the review control does not exist, and
 * therefore the retail send path is currently unprotected by it. That is precisely the fact the
 * Investment and Operations teams need in front of them, so it is stated rather than hidden.
 */
export default function NewsletterTab() {
  return (
    <ReportingErrorBoundary
      surface="the newsletter review tab"
      detail={
        <p className="font-body text-xs text-muted-foreground">
          Note that the review-before-send control is not fully built in this deployment in any
          case — see the Newsletter tab notes once this error clears. No review has been recorded
          for any cycle.
        </p>
      }
    >
      <NewsletterTabInner />
    </ReportingErrorBoundary>
  );
}
